import React from 'react';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { CartItem as CartItemType } from '../types';
import { useCartActions } from '../hooks/useCart';
import { isCustomAmountProduct, getCustomAmountNumber } from '../utils/customAmount';

interface CartItemProps {
  item: CartItemType;
}

const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const { updateQuantity, removeFromCart } = useCartActions();

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity < 1) return;
    try {
      await updateQuantity(item.productId, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemove = async () => {
    try {
      await removeFromCart(item.productId);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  if (!item.product) {
    return (
      <div className="flex items-center space-x-4 p-4 border rounded-lg" style={{ borderColor: '#2a2a2a', backgroundColor: '#1a1a1a' }}>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium tracking-wide" style={{ color: '#e0e0e0' }}>
            ITEM NO LONGER AVAILABLE
          </h3>
          <p className="text-sm" style={{ color: '#a0a0a0' }}>
            This product was removed from the catalog
          </p>
        </div>
        <button
          onClick={handleRemove}
          className="p-1 rounded-full"
          style={{ color: '#ff4444', backgroundColor: '#0f0f0f' }}
          aria-label="Remove unavailable item"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-4 p-4 border rounded-lg" style={{ borderColor: '#2a2a2a', backgroundColor: '#1a1a1a' }}>
      {/* Product Image */}
      <div className="flex-shrink-0">
        <img
          src={item.product.image}
          alt={item.product.name}
          className="w-16 h-16 object-cover rounded-md select-none"
          draggable={false}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'https://via.placeholder.com/64x64?text=No+Image';
          }}
        />
      </div>

      {/* Product Info */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium truncate tracking-wide" style={{ color: '#e0e0e0' }}>
          {item.product.name.toUpperCase()}
        </h3>
        <p className="text-sm truncate" style={{ color: '#a0a0a0' }}>
          {item.product.category}
        </p>
        <p className="text-sm font-medium" style={{ color: '#39ff14' }}>
          {isCustomAmountProduct(item.product) ? '$$$' : formatPrice(item.product.price)}
        </p>
      </div>

      {/* Quantity Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => handleQuantityChange(item.quantity - 1)}
          disabled={item.quantity <= 1}
          className="p-1 rounded-full disabled:opacity-50"
          style={{ color: '#39ff14', backgroundColor: '#0f0f0f' }}
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-8 text-center text-sm font-medium" style={{ color: '#e0e0e0' }}>
          {item.quantity}
        </span>
        <button
          onClick={() => handleQuantityChange(item.quantity + 1)}
          disabled={item.quantity >= item.product.stock}
          className="p-1 rounded-full disabled:opacity-50"
          style={{ color: '#39ff14', backgroundColor: '#0f0f0f' }}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Total Price */}
      <div className="text-sm font-medium" style={{ color: '#39ff14' }}>
        {isCustomAmountProduct(item.product)
          ? `$${(getCustomAmountNumber(item.productId) * item.quantity).toFixed(2)}`
          : formatPrice(item.product.price * item.quantity)}
      </div>

      {/* Remove Button */}
      <button
        onClick={handleRemove}
        className="p-1 rounded-full disabled:opacity-50"
        style={{ color: '#ff4444', backgroundColor: '#0f0f0f' }}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

export default CartItem;
